import { useEffect, useRef, useState } from "react"
import { FileSpreadsheet, Loader2, Search, Upload } from "lucide-react"
import { CLIENTS_SOURCE_LABELS, type ClientImportReport, type ClientImportRow } from "../../shared/domain"
import { Empty, Panel } from "@/components/common"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { apiErrorMessage, type ErpClient } from "@/lib/api"
import { useApp } from "@/lib/app-context"
import { FIELD_LABELS, readClientsFile } from "@/lib/excel"

export function ClientsPage() {
  const { settings, go } = useApp()
  const source = settings.clients.source
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Clientes</h2>
        <p className="text-xs text-muted-foreground">
          Fonte: {CLIENTS_SOURCE_LABELS[source]}
          {source === "erp" && settings.clients.erp_provider ? ` (${settings.clients.erp_provider})` : ""}.{" "}
          <button className="underline" onClick={() => go("/definicoes")}>
            Mudar em Administração
          </button>
          . O email é a identificação no portal.
        </p>
      </div>
      {source === "excel" ? <ImportPanel /> : null}
      <ClientList />
    </div>
  )
}

function ImportPanel() {
  const { api, toast } = useApp()
  const input = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [rows, setRows] = useState<ClientImportRow[]>([])
  const [preview, setPreview] = useState<ClientImportReport | null>(null)
  const [result, setResult] = useState<ClientImportReport | null>(null)
  const [busy, setBusy] = useState<"read" | "preview" | "save" | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const pick = async (file: File | undefined) => {
    if (!file) return
    setFileName(file.name)
    setPreview(null)
    setResult(null)
    setProblem(null)
    setBusy("read")
    try {
      const read = await readClientsFile(file)
      if (read.missing.length) {
        setRows([])
        setProblem(`Faltam colunas: ${read.missing.map((m) => FIELD_LABELS[m] ?? m).join(", ")}.`)
        return
      }
      if (read.rows.length === 0) {
        setProblem("O ficheiro não tem linhas de clientes.")
        return
      }
      setRows(read.rows)
      setBusy("preview")
      setPreview(await api.importClients(read.rows, true))
    } catch (e) {
      setProblem(apiErrorMessage(e))
    } finally {
      setBusy(null)
      if (input.current) input.current.value = ""
    }
  }

  const save = async () => {
    setBusy("save")
    try {
      const r = await api.importClients(rows, false)
      setResult(r)
      setPreview(null)
      toast("success", "Clientes importados", `${r.new} novos · ${r.existing} atualizados`)
    } catch (e) {
      toast("error", "A importação falhou", apiErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  const report = result ?? preview

  return (
    <Panel
      title="Importar Excel"
      action={
        <Button variant="outline" size="sm" disabled={!!busy} onClick={() => input.current?.click()}>
          {busy === "read" || busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          Escolher ficheiro
        </Button>
      }
    >
      <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <p className="text-xs text-muted-foreground">
        Colunas: <strong>N.º cliente PHC</strong>, <strong>Estabelecimento</strong> (0 se não houver), <strong>Nome / Empresa</strong>,{" "}
        <strong>Email</strong>, NIF, Telefone, Localidade, Recebe inquéritos (S/N). Primeiro vê-se o que muda; só grava ao confirmar.
      </p>
      {fileName ? <p className="mt-2 text-sm">{fileName} · {rows.length} linhas</p> : null}
      {problem ? <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{problem}</p> : null}

      {report ? (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label={report.dry_run ? "Novos" : "Criados"} value={report.new} />
            <Stat label={report.dry_run ? "Já existentes (atualizam)" : "Atualizados"} value={report.existing} />
            <Stat label="Com erro" value={report.errors.length} tone={report.errors.length ? "bad" : undefined} />
            <Stat label="Avisos" value={report.warnings.length} />
          </div>
          {report.entities ? (
            <p className="text-xs text-muted-foreground">
              Entidades do CRM: {report.entities.created} criadas · {report.entities.linked} ligadas · {report.entities.updated} atualizadas.
            </p>
          ) : null}
          {report.errors.length || report.warnings.length ? (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border/60">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Linha</TableHead>
                    <TableHead className="w-28">Cliente</TableHead>
                    <TableHead>Problema</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.errors.map((e, i) => (
                    <TableRow key={`e${i}`}>
                      <TableCell className="tabular-nums">{e.row}</TableCell>
                      <TableCell>{e.external_id ?? "—"}</TableCell>
                      <TableCell className="text-red-700">{e.message}</TableCell>
                    </TableRow>
                  ))}
                  {report.warnings.map((w, i) => (
                    <TableRow key={`w${i}`}>
                      <TableCell className="tabular-nums">{w.row}</TableCell>
                      <TableCell>{w.external_id ?? "—"}</TableCell>
                      <TableCell className="text-amber-700">{w.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
          {preview ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPreview(null)}>
                Cancelar
              </Button>
              <Button size="sm" disabled={busy === "save" || preview.new + preview.existing === 0} onClick={save}>
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Importar {preview.new + preview.existing} clientes
                {preview.errors.length ? ` (sem as ${preview.errors.length} com erro)` : ""}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Panel>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "bad" }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold tabular-nums ${tone === "bad" ? "text-red-600" : ""}`}>{value}</p>
    </div>
  )
}

function ClientList() {
  const { api, go, toast, settings } = useApp()
  const [search, setSearch] = useState("")
  const [items, setItems] = useState<ErpClient[] | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = async (term: string, after?: string) => {
    setBusy(true)
    try {
      const page = await api.clients(term || undefined, after)
      setItems((prev) => (after && prev ? [...prev, ...page.items] : page.items))
      setCursor(page.next_cursor)
    } catch (e) {
      toast("error", "Não foi possível carregar os clientes", apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  useEffect(() => {
    void load("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Panel title="Clientes">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          void load(search)
        }}
      >
        <Input placeholder="Nome, NIF, email ou n.º" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button variant="outline" type="submit" disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Procurar
        </Button>
      </form>
      {items === null ? null : items.length === 0 ? (
        <Empty>
          {settings.clients.source === "excel"
            ? "Nenhum cliente. Importe o Excel acima."
            : settings.clients.source === "erp"
              ? "Nenhum cliente do ERP. Confirme a integração do ERP no Koortex."
              : "Nenhuma entidade com estes critérios."}
        </Empty>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>N.º</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>NIF</TableHead>
                <TableHead>Inquéritos</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="max-w-[16rem] truncate font-medium">{c.name}</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">{c.external_id}</TableCell>
                  <TableCell className="max-w-[14rem] truncate">{c.email ?? "—"}</TableCell>
                  <TableCell className="tabular-nums">{c.tax_id ?? "—"}</TableCell>
                  <TableCell>{c.metadata?.surveys_opt_in === false ? <span className="text-muted-foreground">Não</span> : "Sim"}</TableCell>
                  <TableCell className="text-right">
                    {c.entity_id ? (
                      <Button variant="ghost" size="sm" onClick={() => go(`/reclamacoes?entity_id=${c.entity_id}`)}>
                        Reclamações
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cursor ? (
            <div className="mt-3 flex justify-center">
              <Button variant="outline" size="sm" disabled={busy} onClick={() => load(search, cursor)}>
                Mais clientes
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  )
}
